import React, { lazy, Suspense, useCallback, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import theme from "./theme";
import SandboxBanner from "./components/SandboxBanner/SandboxBanner";
import OpenInAppBanner from "./components/OpenInAppBanner/OpenInAppBanner";
import ScrollToTopOnNavigate from "./components/Others/ScrollToTopOnNavigate";
import {
  useNavigate,
  useLocation,
  Navigate,
  Routes,
  Route,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import LayoutMarketing from "./components/Layout/LayoutMarketing";
import Layout from "./components/Layout/Layout";
import LayoutClient from "./components/Layout/LayoutClient";
import LayoutCoordinator from "./components/Layout/LayoutCoordinator";
import LayoutFreelanceEc from "./components/Layout/LayoutFreelanceEc";
import LayoutContractor from "./components/Layout/LayoutContractor";
import NavBar from "./components/NavBar/NavBar";
import NavBarClient from "./components/NavBar/NavBarClient";
import NoAuthNavBar from "./components/NavBar/NoAuthNavBar";
import { AlertDialog } from "./components/Custom/AlertDialog";
import { EventRequest } from "./pages/event-request/EventRequest";
import RechartsTestPage from "./pages/RechartsTestPage";
import EchartsTestPage from "./pages/EchartsTestPage";
import { EventPartials } from "./pages/event-partials/EventPartials";
import { EventRequestClient } from "./pages/event-request/EventRequestClient";
import NavBarContractor from "./components/NavBar/NavBarContractor";
import { LogsOfRequestClient } from "./pages/logs-of-request/LogsOfRequestClient";
import { ContractorResources } from "./pages/contractors/opportunities/ContractorResouces";
const BookingAssistancePage = lazy(() => import("./pages/booking-assistance/BookingAssistancePage"));
const EventDetails = lazy(() => import("./pages/event-details/EventDetails"));
const EventReportDetail = lazy(() => import("./pages/event-details/EventReportDetail"));
const EventDetailsClient = lazy(
  () => import("./pages/event-details/EventDetailsClient"),
);
const EventDetailsCoordinator = lazy(
  () => import("./pages/event-details/EventDetailsCoordinator"),
);
const EventEstimates = lazy(
  () => import("./pages/event-estimates/EventEstimates"),
);
const EventEstimatesClient = lazy(
  () => import("./pages/event-estimates/EventEstimatesClient"),
);
const Positions = lazy(() => import("./pages/positions/Positions"));
const Contractors = lazy(() => import("./pages/contractors/Contractors"));
const BigSearch = lazy(() => import("./pages/big-search/BigSearch"));
const RecruitmentPage = lazy(() => import("./pages/recruitment/RecruitmentPage"));
const PublicJobApplicationPage = lazy(
  () => import("./pages/recruitment/PublicJobApplicationPage"),
);
// Deposit Invoices tracker (finance request 2026-09-10, item 11)
const DepositsTracker = lazy(() => import("./pages/deposits/DepositsTracker"));
const UserAdmins = lazy(() => import("./pages/user-admins/UserAdmins"));
const AllUsers = lazy(() => import("./pages/all-users/AllUsers"));
const UserDeleted = lazy(() => import("./pages/user-deleted/UserDeleted"));
const UserClients = lazy(() => import("./pages/user-clients/UserClients"));
const UserFreelance = lazy(
  () => import("./pages/user-freelance/UserFreelance"),
);
const UserCoordinators = lazy(
  () => import("./pages/user-coordinators/UserCoordinators"),
);
const ContractorOnboarding = lazy(
  () => import("./pages/onboarding/ContractorOnboarding"),
);
const VerifyContractors = lazy(
  () => import("./pages/onboarding/VerifyContractors"),
);
const AddContractors = lazy(
  () => import("./pages/add-contractors/AddContractors"),
);
const Profile = lazy(() => import("./pages/profile/Profile"));
const ProfileClient = lazy(() => import("./pages/profile/ProfileClient"));
const ProfileCoordinator = lazy(
  () => import("./pages/profile/ProfileCoordinator"),
);
const CreateEvent = lazy(() => import("./pages/create-event/CreateEvent"));
const PublicCrewRequest = lazy(
  () => import("./pages/create-event/PublicCrewRequest"),
);
const RequestQuote = lazy(
  () => import("./pages/create-event/components/request-quote/RequestQuote"),
);
const Login = lazy(() => import("./pages/login/Login"));
const EventRequestDetail = lazy(
  () => import("./pages/event-request-details/EventRequestDetails"),
);
const EventRequestDetailsClient = lazy(
  () => import("./pages/event-request-details/EventRequestDetailsClient"),
);
const AllEvents = lazy(() => import("./pages/all-events/AllEvents"));
const AllEventsClient = lazy(
  () => import("./pages/client/all-events/AllEvents"),
);
const AllEventsCoordinator = lazy(
  () => import("./pages/all-events/AllEventsCoordinator"),
);
const EventSOW = lazy(() => import("./pages/sow/EventSOW"));
// Auth gate for the shareable/* email-link routes. It renders its children only once
// a token exists, so the (also lazy) page chunk is never even requested while logged
// out — which is what keeps the 401 → "invalid link" flash from happening.
// Show wrap-up email link: tries the app first, then the web page (login-gated inside).
const ShowMediaRoute = lazy(() => import("./pages/show-media/ShowMediaRoute"));
// Daily show check email: each show links here (app first on Android, then the right page).
const ShowLinkRoute = lazy(() => import("./pages/show-link/ShowLinkRoute"));
const RequireAuthForShareableLink = lazy(
  () => import("./components/Others/RequireAuthForShareableLink"),
);
const FindSow = lazy(() => import("./pages/find-sow/FindSow"));
const EventEstimateDetails = lazy(
  () => import("./pages/event-estimate-view/EstimateDetails"),
);
const EventEstimateDetailsClient = lazy(
  () => import("./pages/event-estimate-view/EstimateDetailsClient"),
);
const ContractorAccountSignup = lazy(
  () => import("./pages/contractors/contractor-signup/ContractorAccountSignup"),
);
const ClientSignup = lazy(() => import("./pages/login/ClientSignup"));
const CoordinatorPO = lazy(() => import("./pages/po/CoordinatorPO"));
const Schedule = lazy(
  () => import("./pages/contractor-side/ScheduleTab/Schedule"),
);
const PrivacyPolicy = lazy(() => import("./pages/public/PrivacyPolicy"));
const DeleteAccount = lazy(() => import("./pages/public/DeleteAccount"));
const TermsAndConditions = lazy(
  () => import("./pages/public/TermsAndConditions/TermsAndConditions"),
);
const ProfileContractor = lazy(
  () => import("./pages/profile/ProfileContractor"),
);

const Notifications = lazy(
  () => import("./pages/NotificationsTab/Notifications"),
);

const EventChatAdmin = lazy(
  () => import("./pages/messaging/EventChatAdmin/EventChatAdmin"),
);

const ClientChatAdmin = lazy(
  () => import("./pages/messaging/ClientChatAdmin/ClientChatAdmin"),
);

const EventChatClient = lazy(
  () => import("./pages/messaging/EventChatClient/EventChatClient"),
);

const AdminChatClient = lazy(
  () => import("./pages/messaging/AdminChatClient/AdminChatClient"),
);

import { useJsApiLoader } from "@react-google-maps/api";
import { GOOGLE_MAPS_PACKAGE_ON_LOAD } from "./store/constants/googleMapsPackageConstants";
const ReportsPage = lazy(() => import("./pages/reports/ReportPage"));
const PayrollReport = lazy(
  () => import("./pages/reports/payroll-reports/PayrollReport"),
);
const RevenueReport = lazy(() => import("./pages/reports/revenue-report"));
const CommissionsReport = lazy(
  () => import("./pages/reports/commissions"),
);
import storeEmailRedirectionContext from "./utils/emailRedirection/storeEmailRedirectionContext";
import { CLEAR_EMAIL_REDIRECTION_REDUCER } from "./store/constants/emailRedirectionConstants";
import { isUserRole, isActiveRole } from "./utils/methods/storeFunc";
import { USER_ROLES } from "./staticData/userRoles";
import Dashboard from "./pages/dashboard/Dashboard";
import { ContractorAllOpportunities } from "./pages/contractors/opportunities/ContractorAllOpportunities";
const Configuration = lazy(() => import("./pages/configuration/Configuration"));
const SafetyAndHarassmentVideos = lazy(
  () =>
    import("./pages/configuration/SafetyAndHarassmentVideos/SafetyAndHarassmentVideos"),
);
import "video.js/dist/video-js.css";
import "react-day-picker/style.css";
import { PreBookContractors } from "./pages/pre-book/PreBookContractor";
const AirportsList = lazy(
  () => import("./pages/configuration/airports/AirportList"),
);
const QRGeneration = lazy(() => import("./pages/generate-qr/QRGeneration"));
const ContractorInvite = lazy(
  () => import("./pages/contractorInvite/ContractorInvite"),
);
const AcceptInvite = lazy(() => import("./pages/accept-invite/AcceptInvite"));
import { intakeScreens } from "./utils/constants/intakeScreens";
const RequestedQuote = lazy(
  () => import("./pages/requested-quote/RequestedQuote"),
);
import { fetchReimbursementCategory } from "./store/actions/eventRequestDetails";
import {
  alertDialogAciton,
  fetchContractorProfileDetailsAction,
  firebaseSupportStatusAction,
  getUserProfileDataAciton,
  logoutAction,
  resetAuthAction,
} from "./store/actions/auth";
const TutorialVideo = lazy(
  () => import("./pages/configuration/TutorialVideo/TutorialVideo"),
);
const SystemNotifications = lazy(
  () => import("./pages/configuration/SystemNotifications/SystemNotifications"),
);
const SatisfactionSurveyConfig = lazy(
  () =>
    import("./pages/configuration/satisfaction-survey/SatisfactionSurveyConfig"),
);
const SupportInbox = lazy(
  () => import("./pages/configuration/support/SupportInbox"),
);
const SupportChat = lazy(() => import("./pages/support/SupportChat"));
const SupportConfig = lazy(
  () => import("./pages/configuration/support/SupportConfig"),
);
const SurveyResponsePage = lazy(
  () => import("./pages/survey-response/SurveyResponsePage"),
);
const AllContracts = lazy(() => import("./pages/contract/AllContracts"));
const AllContractsClient = lazy(
  () => import("./pages/contract/AllContractsClient"),
);
import { ContractDetails } from "./pages/contract-details/ContractDetails";
import { getSignatureAction } from "./store/actions/contract/contract";

import { CongratulationsNavButtonsVariant1 } from "./pages/create-event/components/event-details/EventDetails";

import {
  homeButtonHandler,
  newEventHandler,
} from "./pages/create-event/handlers/congratulationHandlers";

// non modular css files
import "./components/DotPagination/dotPagination.css";
import "./components/SideBar/sidebar.css";
import "./components/Layout/Layout.css";
import "./assets/css/common.css";
import "./pages/user-clients/userClients.css";
import "./pages/event-details/EventDetails.css";
import "./pages/add-contractors/components/AddPositionModal.css";
import "./pages/find-sow/findSow.css";
import "./pages/create-event/components/sessions/step2/Step2.css";
import "./pages/create-event/components/sessions/sessions.css";
import "./pages/user-admins/userAdmins.css";
import "./pages/user-coordinators/userCoordinators.css";
import "./pages/event-estimates/eventEstimates.css";
import "./pages/event-estimate-view/eventEstimateView.css";
import "./pages/profile/profile.css";
import "./pages/positions/position.css";
import "./pages/event-request-details/components/editSessionModal.css";
import "./pages/event-request-details/eventRequestDetails.css";
import "./pages/event-request/EventRequests.css";
import "./pages/contractors/contractors.css";
import "./pages/login/components/otpInput.css";
import "./pages/event-partials/eventPartials.css";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
const QrData = lazy(() => import("./pages/contractor-open/QrData"));
import { useEmitUserId } from "./customHooks/socketEvents/sendUserId/useEmitUserId";
import { ReportModal } from "./components/Sms/ReportModal";
import { SmsHistoryEventModal } from "./components/Sms/SmsHistoryEventModal";
import useDisableScrollOnNumberInput from "./customHooks/generalHooks/useDisableScrollOnNumberInput";
import { NO_NAVBAR_URLS } from "./utils/constants/urls";
import { Box } from "@mui/material";
const Integration = lazy(() => import("./design/pages/Integration"));
const LiveEvent = lazy(() => import("./design/pages/LiveEvent"));
import { FRONTEND_CONFIG } from "./utils/constants/configs";
import { DirectSMSModal } from "./components/Others/DirectSMSModal";
import {
  getDeductionCategoriesAction,
  getNonTaxableCategoriesAction,
  getReimbursementCategoriesAction,
} from "./store/actions/event/adjustments";
import EventAdjustmentWrapper from "./pages/event-details/components/reusableComponents/EventAdjustmentWrapper";
// import { SmsConsentPublic } from "./pages/public/sms-consent/SmsConsentPublic";
const SmsConsentPublicWrapper = lazy(
  () => import("./design/pages/SmsConsentPublic"),
);
import { useQrAppPrompt } from "./customHooks/generalHooks/useQrAppPrompt";
const EventRates = lazy(() => import("./design/pages/EventRates"));
const NavBarFreelanceEc = lazy(
  () => import("./components/NavBar/NavBarFreelanceEc"),
);
import { useDeviceId } from "./customHooks/generalHooks/useDeviceId";
import { useBackAndForeGround } from "./customHooks/generalHooks/useBackAndForeGround";
import { useProvisionalContractorSignupAppPrompt } from "./customHooks/generalHooks/useContractorSignupAppPrompt";
import ContractorLinks from "./pages/contractor-links/ContractorLinks";
import {
  fetchContractorDocumentUrls,
  getContractorLinkAction,
} from "./store/actions/config/contractorLink";
import posthog from "posthog-js";
import {
  BackStagePassConsent1099,
  ElectronicExecutionConsent1099,
  IndependentContractorAgreementConsent1099,
  MediaReleaseConsent1099,
  PaymentTermsAcknowledgementConsent1099,
  TextCommunicationConsent1099,
} from "./pages/contractor-consent/ContractorConsent";
import { isSupported } from "firebase/messaging";
import { DocAndContractsViewer } from "./pages/contractors/components/AddContractorForms/_components/DocAndContractsViewer";
import NotFound from "./pages/not-found/NotFound";
import { ManageApprovals } from "./components/Tooltip/ManageApprovals";
const MarketingUsers = lazy(() => import("./pages/marketing/MarketingUsers"));
const MarketingProfile = lazy(() => import("./pages/marketing/MarketingProfile"));
const ShowMediaGalleryPage = lazy(() => import("./pages/show-media-gallery/ShowMediaGalleryPage"));
const MediaGalleryEvent = lazy(
  () => import("./pages/event-details/components/media/MediaGalleryEvent"),
);
const EventDetailsFreelanceEc = lazy(
  () => import("./pages/event-details/EventDetailsFreelanceEc"),
);
const AllEventsFreelance = lazy(
  () => import("./pages/all-events/AllEventsFreelance"),
);
const ProfileFreelanceEc = lazy(
  () => import("./pages/profile/ProfileFreelance"),
);
const RateList = lazy(() => import("./pages/rate-list/RateList"));
const ServiceAgreements = lazy(
  () => import("./pages/configuration/ServiceAgreements/ServiceAgreements"),
);
const FreelancePO = lazy(() => import("./pages/po/FreelancePO"));
const ShareableDepositInvoice = lazy(
  () => import("./pages/deposit-invoice-shareable/ShareableDepositInvoice"),
);
const ShareableInvoice = lazy(
  () => import("./pages/invoice-shareable/SharebleInvoice"),
);
const ShareableEstimate = lazy(
  () => import("./pages/estimate-shaerable/ShareableEstimate"),
);

/**
 * Catch-all fallback (`path="*"`) for an authenticated user.
 *
 * There are short windows where the current URL has no matching <Route> without
 * that being an error: (1) redux-persist rehydration on the initial load and (2) the
 * ~150ms gap during a role-switch before `window.location.replace("/")`. In those
 * cases we show a spinner to avoid a 404 flash.
 *
 * But if the route NEVER matches (an invalid/legacy URL typed by hand — e.g.
 * `/all-events` instead of `/event/all-events`), the spinner cannot spin
 * forever, otherwise the symptom becomes an "eternal Loading…". After a short grace
 * we fall through to the real NotFound. If the route matches before that, this component
 * unmounts (`<Routes>` starts rendering the correct block) and NotFound never
 * appears — the role-switch reloads in 150ms, well below the grace.
 */
const AuthedRouteFallback = () => {
  const [graceElapsed, setGraceElapsed] = React.useState(false);

  React.useEffect(() => {
    const t = setTimeout(() => setGraceElapsed(true), 1500);
    return () => clearTimeout(t);
  }, []);

  if (graceElapsed) return <NotFound />;

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "#000",
        color: "#fff",
        gap: 16,
      }}
    >
      <div
        style={{
          width: 48,
          height: 48,
          border: "3px solid rgba(255,255,255,0.15)",
          borderTopColor: "#00EEC5",
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
        }}
      />
      <div style={{ fontSize: 14, opacity: 0.7 }}>Loading…</div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

const App = () => {
  const libraries = React.useMemo(() => {
    return ["places"];
  }, []);

  const dispatch = useDispatch();

  // react-router hooks start...
  const navigate = useNavigate();
  const location = useLocation();
  // react-router hooks end...

  // Selectors start...
  const auth = useSelector((store) => store.auth);
  const emailRedirection = useSelector((store) => store.emailRedirection);
  const googleMapsPackage = useSelector((store) => store.googleMapsPackage);
  // Selectors end...

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_API_KEY,
    id: "google-maps",
    libraries,
  });

  // generate device id
  useDeviceId();

  useEmitUserId();
  // hook to disable scroll on number type element
  useDisableScrollOnNumberInput();
  // hook to redirect from qr code
  useQrAppPrompt();
  // handle provisional contractor signup
  useProvisionalContractorSignupAppPrompt();

  // hook to emit on background and foreground
  useBackAndForeGround();

  const firebaseSupport = useCallback(async () => {
    // single time check if this browser supports firebase notifications or not
    const doesSupportFirebaseSDK = await isSupported();
    const isNotificationPermission = Notification.permission;

    dispatch(
      firebaseSupportStatusAction({
        isFirebaseSupported: doesSupportFirebaseSDK,
        isNotificationDenied: isNotificationPermission == "denied",
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    firebaseSupport();
  }, [firebaseSupport]);

  React.useEffect(() => {
    if (
      googleMapsPackage?.isLoaded !== isLoaded ||
      googleMapsPackage?.loadError !== loadError
    ) {
      dispatch({
        type: GOOGLE_MAPS_PACKAGE_ON_LOAD,
        payload: {
          isLoaded,
          loadError,
        },
      });
    }
  }, [
    isLoaded,
    loadError,
    dispatch,
    googleMapsPackage?.isLoaded,
    googleMapsPackage?.loadError,
  ]);

  React.useEffect(() => {
    if (location?.pathname === emailRedirection?.path) {
      dispatch({
        type: CLEAR_EMAIL_REDIRECTION_REDUCER,
      });
    }

    if (
      emailRedirection?.forUser && auth?.userId
        ? auth?.userId !== emailRedirection?.forUser
        : false
    ) {
      dispatch(
        alertDialogAciton({
          open: true,
          title: "Warning",
          message:
            "The email link wasn't for this user, this might not work properly for this user",
        }),
      );

      dispatch({
        type: CLEAR_EMAIL_REDIRECTION_REDUCER,
      });
    }
    // eslint-disable-next-line
  }, [location?.pathname]);

  React.useEffect(() => {
    if (location?.search?.split("?")?.[1]?.includes?.("email=true")) {
      storeEmailRedirectionContext(location?.search?.split("?")?.[1], navigate);
    }
    // eslint-disable-next-line
  }, [location?.search]);

  React.useEffect(() => {
    if (auth?.userId) {
      // dispatch(fetchReimbursementCategory({}));
      if (
        isUserRole(
          USER_ROLES.SUPERADMIN,
          USER_ROLES.ADMIN,
          USER_ROLES.COORDINATOR,
        )
      ) {
        dispatch(getReimbursementCategoriesAction({}));
        dispatch(getDeductionCategoriesAction({}));
        dispatch(getNonTaxableCategoriesAction({}));
      }
      // posthog.identify(auth?.userId);
      // posthog.people.set({
      //   name: auth?.fullName
      //     ? auth?.fullName
      //     : `${auth?.firstName} ${auth?.lastName}`,
      // });
      if (FRONTEND_CONFIG.contracts && !isActiveRole(USER_ROLES.FREELANCE_EC, USER_ROLES.MARKETING)) {
        dispatch(getSignatureAction({}));
      }
    }
    // eslint-disable-next-line
  }, [auth?.userId, dispatch]);

  React.useEffect(() => {
    // Defensive: only reset if there is NEITHER userId NOR token. Avoids a race
    // condition where LOGIN_SUCCESS has already set the token but userId has not yet
    // propagated into the reference captured by the closure.
    if (!auth?.userId && !auth?.userToken) {
      dispatch(resetAuthAction());
    } else if (auth?.userId) {
      setTimeout(() => {
        dispatch(
          getUserProfileDataAciton({
            successCallback: () => {
              // Multi-role: uses activeRole (current UI). It used to be isUserRole,
              // which fired for any multi-role user with an approved contractor
              // — if contractor is not the primary role (e.g. a superAdmin who
              // has an approved contractor for testing), the sub-doc's
              // isApproved=false forced a LOGOUT even though the user was valid.
              if (isActiveRole(USER_ROLES.CONTRACTOR)) {
                dispatch(
                  fetchContractorProfileDetailsAction({
                    payload: {
                      contractorId: auth?.userId,
                    },
                    successCallback: ({ response }) => {
                      if (response?.data?.data?.isApproved) return;

                      // Contractor NOT yet approved (status "invited" or pending):
                      // keep them logged in so they can respond to opportunity invites
                      // (legacy blocked them with a forced-logout — that broke the
                      // invite flow because the contractor needs to log in at least
                      // once before the admin approves). The `disableDashboard`
                      // set in LOGIN_SUCCESS already redirects to /profile and
                      // blocks the rest of the app via route guards.
                    },
                  }),
                );
              }
            },
          }),
        );

        // fetch contractor contract urls
        if (!isActiveRole(USER_ROLES.MARKETING)) {
          dispatch(fetchContractorDocumentUrls());
        }
        if (
          // auth?.addAdpLink ||
          isUserRole(
            USER_ROLES.SUPERADMIN,
            USER_ROLES.ADMIN,
            USER_ROLES.FREELANCE_EC,
            USER_ROLES.COORDINATOR,
            USER_ROLES.CONTRACTOR,
          )
        ) {
          dispatch(getContractorLinkAction({}));
        }
      }, 2000);
    }
  }, [auth?.userId, auth?.addAdpLink, auth?.canManageGusto, dispatch]);

  return (
    <div
      style={{
        margin: "auto",
        // maxWidth: "2000px",
      }}
    >
      <SandboxBanner />
      <ThemeProvider theme={theme}>
        {NO_NAVBAR_URLS?.includes(
          location.pathname,
        ) ? null : !auth?.userToken ? (
          <NoAuthNavBar />
        ) : isActiveRole(USER_ROLES.ADMIN, USER_ROLES.SUPERADMIN) ? (
          <NavBar />
        ) : isActiveRole(USER_ROLES.MARKETING) ? (
          <NavBar accountOnly />
        ) : isActiveRole(USER_ROLES.CONTRACTOR) ? (
          <NavBarContractor />
        ) : isActiveRole(USER_ROLES.COORDINATOR, USER_ROLES.CLIENT) ? (
          <NavBarClient />
        ) : isActiveRole(USER_ROLES.FREELANCE_EC) ? (
          <NavBarFreelanceEc />
        ) : null}
        <Suspense fallback={<div></div>}>
          <Routes>
            {/* Base route redirection to create-event */}
            <Route
              path="/"
              element={
                location?.search
                  ?.split("?")?.[1]
                  ?.includes?.("email=true") ? null : (
                  <Navigate
                    to={
                      // Multi-role: each activeRole goes straight to its own
                      // landing. Previously only CONTRACTOR was handled and the rest
                      // fell through to /login (causing a 404 flash before Login
                      // redirected again).
                      !auth?.userToken
                        ? "/login"
                        : isActiveRole(USER_ROLES.MARKETING)
                          ? "/show-media-gallery"
                          : isActiveRole(
                              USER_ROLES.ADMIN,
                              USER_ROLES.SUPERADMIN,
                            )
                          ? "/dashboard"
                          : isActiveRole(USER_ROLES.CLIENT)
                            ? "/dashboard"
                            : isActiveRole(USER_ROLES.COORDINATOR)
                              ? "/dashboard"
                              : isActiveRole(USER_ROLES.FREELANCE_EC)
                                ? "/event/all-events"
                                : isActiveRole(USER_ROLES.CONTRACTOR)
                                  ? auth?.disableDashboard
                                    ? "/onboarding"
                                    : "/opportunities"
                                  : "/login"
                    }
                  />
                )
              }
            />

            <Route path="privacy-policy" element={<PrivacyPolicy />} />
            {/* Public account deletion (store requirement). Same POST /deleteAccount as the app. */}
            <Route path="delete-account" element={<DeleteAccount />} />
            <Route
              path="terms-and-conditions"
              element={<TermsAndConditions />}
            />
            <Route path="request-quote" element={<RequestQuote />} />
            <Route path="sms-consent" element={<SmsConsentPublicWrapper />} />
            <Route path="careers/:publicId" element={<PublicJobApplicationPage />} />
            {/* Recharts Test Page Route */}
            <Route path="recharts-test" element={<RechartsTestPage />} />
            {/* ECharts Test Page Route */}
            <Route path="echarts-test" element={<EchartsTestPage />} />
            {/* Public Routes */}
            {!auth?.userToken ? (
              <>
                <Route path="login" element={<Login />} />
                <Route
                  path="contractor-invite"
                  element={
                    <Login intakeScreen={intakeScreens.contractorInvite} />
                  }
                />
              </>
            ) : (
              <>
                <Route
                  path="contractor-invite"
                  element={
                    <Login intakeScreen={intakeScreens.contractorInvite} />
                  }
                />
                <Route
                  path="login"
                  element={
                    <Navigate
                      state={
                        (
                          emailRedirection?.forUser
                            ? emailRedirection?.forUser == auth?.userId
                            : true
                        )
                          ? emailRedirection?.data
                          : {}
                      }
                      to={
                        emailRedirection?.path &&
                        (emailRedirection?.forUser
                          ? emailRedirection?.forUser == auth?.userId
                          : true)
                          ? emailRedirection?.path
                          : isActiveRole(USER_ROLES.MARKETING)
                            ? "/show-media-gallery"
                            : isActiveRole(USER_ROLES.ADMIN, USER_ROLES.SUPERADMIN)
                            ? "/dashboard"
                            : isActiveRole(USER_ROLES.CLIENT)
                              ? "/dashboard"
                              : isActiveRole(USER_ROLES.COORDINATOR)
                                ? "/dashboard"
                                : isActiveRole(USER_ROLES.FREELANCE_EC)
                                  ? "/event/all-events"
                                  : isActiveRole(USER_ROLES.CONTRACTOR)
                                    ? !auth?.disableDashboard
                                      ? "/opportunities"
                                      : "/onboarding"
                                    : ""
                      }
                    />
                  }
                />
              </>
            )}
            <Route
              path="create-event"
              element={
                !auth?.userToken ? (
                  <PublicCrewRequest />
                ) : isUserRole(
                  USER_ROLES.ADMIN,
                  USER_ROLES.SUPERADMIN,
                  USER_ROLES.CLIENT,
                  USER_ROLES.COORDINATOR,
                ) ? (
                  <Navigate to="/dashboard" state={{ create: false }} />
                ) : isActiveRole(USER_ROLES.MARKETING) ? (
                  <Navigate to="/show-media-gallery" replace />
                ) : !isActiveRole(USER_ROLES.CONTRACTOR) ? (
                  <CreateEvent
                    showNavigationButtons={true}
                    NavButtonsComponent={CongratulationsNavButtonsVariant1}
                    navButtonsComponentProps={{
                      homeButtonHandler,
                      newEventHandler,
                    }}
                  />
                ) : (
                  <Navigate to="/profile" />
                )
              }
            />
            {/* {!auth?.userToken ? ( */}
            <Route path="/contractor-signup">
              <Route path="" element={<ContractorAccountSignup />}></Route>
            </Route>
            <Route path="/client-signup" element={<ClientSignup />} />
            {/* ) : null} */}
            {/* <Route path="congratulations" element={<Congratulations />} /> */}
            {/* <Route path="signup" element={<ContractorSignup />} /> */}

            {/* Private Routes */}
            {/* Admin Routes */}
            {isActiveRole(USER_ROLES.ADMIN, USER_ROLES.SUPERADMIN) ? (
              <Route element={<Layout />}>
                <Route path="/event">
                  <Route path="new-requests" element={<EventRequest />} />
                  <Route path="partials" element={<EventPartials />} />
                  <Route
                    path="new-request/details"
                    element={<EventRequestDetail />}
                  />
                  <Route path="all-events" element={<AllEvents />} />
                  <Route path="details" element={<EventDetails />} />
                  <Route path="estimates" element={<EventEstimates />} />
                  <Route
                    path="pre-book-contractors"
                    element={<PreBookContractors />}
                  />
                  {/* <Route path="estimates/view" element={<EventEstimateView />} /> */}
                  <Route
                    path="estimates/view"
                    element={<EventEstimateDetails />}
                  />
                  {/* <Route path="partial-requests" element={<EventPartials />} /> */}
                  <Route path="sow" element={<EventSOW />} />
                  <Route path="po" element={<CoordinatorPO />} />
                  <Route path="freelancerPO" element={<FreelancePO />} />
                  <Route path="chat" element={<EventChatAdmin />} />

                  <Route path="chat/admin" element={<ClientChatAdmin />} />
                </Route>

                <Route path="/dashboard">
                  <Route
                    path="new-request/details"
                    element={<EventRequestDetail />}
                  />
                  <Route
                    path="estimates/view"
                    element={<EventEstimateDetails />}
                  />
                  <Route path="details" element={<EventDetails />} />
                  <Route path="" element={<Dashboard />} />
                </Route>

                <Route path="contracts" element={<AllContracts />} />
                <Route path="contract-details" element={<ContractDetails />} />

                <Route
                  path="positions"
                  element={
                    <Positions
                      readOnly={
                        isUserRole(USER_ROLES.SUPERADMIN)
                          ? false
                          : !auth?.canPositionEdit
                      }
                    />
                  }
                />
                <Route path="requested-quote" element={<RequestedQuote />} />
                <Route path="contractors" element={<Contractors />} />
                <Route
                  path="recruitment"
                  element={
                    isUserRole(USER_ROLES.SUPERADMIN) ||
                    auth?.canManageRecruitment ? (
                      <RecruitmentPage />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )
                  }
                />
                <Route path="find-sow" element={<FindSow />} />
                {FRONTEND_CONFIG.mediaGalleryAllEvents && (
                  <Route path="media-gallery" element={<MediaGalleryEvent />} />
                )}
                {/* Show Media across all events (admin/superAdmin). */}
                <Route path="show-media-gallery" element={<ShowMediaGalleryPage />} />
                <Route
                  path="reports/contractor-work"
                  element={<ReportsPage />}
                />
                <Route
                  path="reports/assigned-by-users"
                  element={<ReportsPage />}
                />
                {/* Big Search — pessoas <-> eventos (docs/plans/PLAN_big-search.md) */}
                <Route path="big-search" element={<BigSearch />} />
                {/* Deposit Invoices tracker — superAdmin or admin with canManageDeposits. */}
                <Route
                  path="deposits"
                  element={
                    isUserRole(USER_ROLES.SUPERADMIN) ||
                    auth?.canManageDeposits ? (
                      <DepositsTracker />
                    ) : (
                      <Navigate to="/dashboard" replace />
                    )
                  }
                />
                {FRONTEND_CONFIG.revenueReport && (
                  <Route path="reports/revenue" element={<RevenueReport />} />
                )}
                {FRONTEND_CONFIG.payrollReport && (
                  <Route path="reports/payroll" element={<PayrollReport />} />
                )}
                {isUserRole(USER_ROLES.SUPERADMIN, USER_ROLES.ADMIN) && (
                  <Route
                    path="reports/commissions"
                    element={<CommissionsReport />}
                  />
                )}
                <Route path="contractors/add" element={<AddContractors />} />
                <Route path="contractors/verify" element={<VerifyContractors />} />
                <Route path="/users">
                  {/* All Users (multi-role unified) — superAdmin ONLY.
                      Exposes every user plus the role/delete controls; the
                      backend GET /users enforces the same. */}
                  {isUserRole(USER_ROLES.SUPERADMIN) && (
                    <Route path="all" element={<AllUsers />} />
                  )}
                  {isUserRole(USER_ROLES.SUPERADMIN) ? (
                    <>
                      <Route path="admins" element={<UserAdmins />} />
                      <Route path="marketing" element={<MarketingUsers />} />
                    </>
                  ) : null}
                  <Route path="coordinators" element={<UserCoordinators />} />
                  <Route path="clients" element={<UserClients />} />
                  <Route path="freelance" element={<UserFreelance />} />

                  {isUserRole(USER_ROLES.SUPERADMIN, USER_ROLES.ADMIN) && (
                    <Route path="deleted" element={<UserDeleted />}></Route>
                  )}
                </Route>
                <Route path="/notifications" element={<Notifications />} />
                <Route
                  path="configuration/text-templates"
                  element={<Configuration />}
                />
                <Route
                  path="configuration/safety-and-non-harassment-videos"
                  element={<SafetyAndHarassmentVideos />}
                />
                <Route
                  path="configuration/tutorial-video"
                  element={<TutorialVideo />}
                />

                <Route
                  path="configuration/airports"
                  element={<AirportsList />}
                />
                <Route path="configuration/rate-list" element={<RateList />} />
                {/* Finance item 10: audit list of Service Agreement acceptances (admin). */}
                <Route
                  path="configuration/service-agreements"
                  element={<ServiceAgreements />}
                />
                {isUserRole(USER_ROLES.SUPERADMIN) || auth?.addAdpLink ? (
                  <Route
                    path="configuration/contractor-links"
                    element={<ContractorLinks />}
                  />
                ) : null}
                {isUserRole(USER_ROLES.SUPERADMIN) ? (
                  <Route
                    path="configuration/system-notifications"
                    element={<SystemNotifications />}
                  />
                ) : null}
                <Route
                  path="configuration/event-rates"
                  element={<EventRates />}
                />
                <Route
                  path="configuration/satisfaction-survey"
                  element={<SatisfactionSurveyConfig />}
                />
                <Route path="configuration/support" element={<SupportConfig />} />
                <Route path="support-inbox" element={<SupportInbox />} />
                <Route path="support" element={<SupportChat />} />
                <Route path="profile" element={<Profile />} />
                <Route path="generate-qr" element={<QRGeneration />} />
              </Route>
            ) : null}
            {auth?.userToken && isActiveRole(USER_ROLES.MARKETING) && (
              <Route element={<LayoutMarketing />}>
                <Route path="show-media-gallery" element={<ShowMediaGalleryPage />} />
                <Route path="profile" element={<MarketingProfile />} />
              </Route>
            )}
            {/* Admin Routes */}

            {/* Client Routes */}
            {isActiveRole(USER_ROLES.CLIENT) ? (
              <Route element={<LayoutClient />}>
                <Route path="support" element={<SupportChat />} />
                <Route path="survey/:accessId" element={<SurveyResponsePage />} />
                <Route path="event">
                  <Route path="new-requests" element={<EventRequestClient />} />
                  <Route path="partials" element={<EventPartials />} />
                  <Route
                    path="new-request/details"
                    element={<EventRequestDetailsClient />}
                  />
                  <Route path="all-events" element={<AllEventsClient />} />
                  <Route path="details" element={<EventDetailsClient />} />
                  <Route path="estimates" element={<EventEstimatesClient />} />
                  {/* <Route path="estimates/view" element={<EventEstimateViewClient />} /> */}
                  <Route
                    path="estimates/view"
                    element={<EventEstimateDetailsClient />}
                  />

                  <Route path="chat" element={<EventChatClient />} />

                  <Route path="chat/admin" element={<AdminChatClient />} />
                </Route>

                <Route path="/dashboard">
                  <Route
                    path="new-request/details"
                    element={<EventRequestDetailsClient />}
                  />
                  <Route
                    path="estimates/view"
                    element={<EventEstimateDetailsClient />}
                  />
                  <Route path="details" element={<EventDetailsClient />} />
                  <Route path="" element={<Dashboard />} />
                </Route>
                <Route path="/notifications" element={<Notifications />} />
                <Route path="profile" element={<ProfileClient />} />
                <Route path="logs-of-event" element={<LogsOfRequestClient />} />
                <Route
                  path="configuration/tutorial-video"
                  element={<TutorialVideo />}
                />
                <Route path="configuration/rate-list" element={<RateList />} />
                <Route
                  path="configuration/event-rates"
                  element={<EventRates />}
                />
                {/* <Route path="/contract"> */}
                {/*   <Route */}
                {/*     path="new" */}
                {/*     element={<AllContractsClient type={"new"} />} */}
                {/*   /> */}
                {/*   <Route */}
                {/*     path="under-review" */}
                {/*     element={<AllContractsClient type={"under-review"} />} */}
                {/*   /> */}
                {/*   <Route */}
                {/*     path="verified" */}
                {/*     element={<AllContractsClient type={"verified"} />} */}
                {/*   /> */}
                {/* </Route> */}
                <Route
                  path="/contracts"
                  element={<AllContractsClient type="All" />}
                />
                <Route
                  path="/contract-details"
                  element={<ContractDetails isClient={true} />}
                />
              </Route>
            ) : null}
            {/* Client Routes */}

            {/* Coordinator Routes */}
            {isActiveRole(USER_ROLES.COORDINATOR) ? (
              <Route element={<LayoutCoordinator />}>
                  <Route path="booking-assistance" element={<BookingAssistancePage />} />
                <Route path="support" element={<SupportChat />} />
                <Route path="/event">
                  <Route path="new-requests" element={<EventRequestClient />} />
                  {/*    <Route
                      path="new-request/details"
                      element={<EventRequestDetailsClient />}
                    /> */}
                  <Route path="all-events" element={<AllEventsCoordinator />} />
                  <Route path="details" element={<EventDetailsCoordinator />} />
                  <Route
                    path="new-request/details"
                    element={<EventDetailsCoordinator isBeforeApprove />}
                  />
                  <Route
                    path="estimates/view"
                    element={<EventDetailsCoordinator isBeforeApprove />}
                  />
                  <Route path="sow" element={<EventSOW />} />
                  <Route path="freelancerPO" element={<FreelancePO />} />
                  <Route path="po" element={<CoordinatorPO />} />
                  <Route path="chat" element={<EventChatAdmin />} />

                  <Route path="chat/admin" element={<AdminChatClient />} />
                  {/* <Route
                      path="estimates"
                      element={<EventEstimatesClient />}
                    /> */}
                  {/* <Route path="estimates/view" element={<EventEstimateViewClient />} /> */}
                  {/* <Route
                      path="estimates/view"
                      element={<EventEstimateDetailsClient />}
                    /> */}
                </Route>
                <Route path="/users">
                  <Route path="coordinators" element={<UserCoordinators />} />
                </Route>
                <Route path="dashboard">
                  <Route path="" element={<Dashboard />} />
                  <Route path="details" element={<EventDetailsCoordinator />} />
                  <Route
                    path="new-request/details"
                    element={<EventDetailsCoordinator isBeforeApprove />}
                  />
                  <Route
                    path="estimates/view"
                    element={<EventDetailsCoordinator isBeforeApprove />}
                  />
                </Route>
                <Route
                  path="positions"
                  element={<Positions readOnly={!auth?.canPositionEdit} />}
                />
                <Route
                  path="contractors"
                  element={
                    <Contractors
                      readOnly={{
                        addContractor: false,
                        editContractor: false,
                        deleteContractor: true,
                      }}
                    />
                  }
                />
                <Route path="contractors/add" element={<AddContractors />} />
                <Route path="contractors/verify" element={<VerifyContractors />} />
                <Route path="find-sow" element={<FindSow />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="configuration">
                  <Route path="text-templates" element={<Configuration />} />
                </Route>
                <Route
                  path="configuration/tutorial-video"
                  element={<TutorialVideo />}
                />
                {/* <Route path="/contract"> */}
                {/*   <Route */}
                {/*     path="new" */}
                {/*     element={<AllContractsClient type={"new"} />} */}
                {/*   /> */}
                {/*   <Route */}
                {/*     path="under-review" */}
                {/*     element={<AllContractsClient type={"under-review"} />} */}
                {/*   /> */}
                {/*   <Route */}
                {/*     path="verified" */}
                {/*     element={<AllContractsClient type={"verified"} />} */}
                {/*   /> */}
                {/* </Route> */}

                <Route
                  path="/contracts"
                  element={<AllContractsClient type="All" />}
                />
                <Route
                  path="/contract-details"
                  element={<ContractDetails isClient={true} />}
                />
                <Route path="profile" element={<ProfileCoordinator />} />
                <Route path="generate-qr" element={<QRGeneration />} />
              </Route>
            ) : null}
            {/* Coordinate Routes */}

            {/* Freelance Ec Routes */}
            {isActiveRole(USER_ROLES.FREELANCE_EC) ? (
              <Route element={<LayoutFreelanceEc />}>
                  <Route path="booking-assistance" element={<BookingAssistancePage />} />
                <Route path="support" element={<SupportChat />} />
                <Route path="/event">
                  <Route path="all-events" element={<AllEventsFreelance />} />
                  <Route path="details" element={<EventDetailsFreelanceEc />} />
                  <Route path="sow" element={<EventSOW />} />
                  {/* <Route path="freelancerPO" element={<FreelancePO />} /> */}
                  {/* <Route path="po" element={<CoordinatorPO />} /> */}
                  <Route path="chat" element={<EventChatAdmin />} />
                  <Route path="chat/admin" element={<AdminChatClient />} />
                  {/* <Route
                      path="estimates"
                      element={<EventEstimatesClient />}
                    /> */}
                  {/* <Route path="estimates/view" element={<EventEstimateViewClient />} /> */}
                  {/* <Route
                      path="estimates/view"
                      element={<EventEstimateDetailsClient />}
                    /> */}
                </Route>
                {/* <Route path="/users"> */}
                {/*   <Route path="coordinators" element={<UserCoordinators />} /> */}
                {/* </Route> */}
                {/* <Route path="dashboard"> */}
                {/*   <Route path="" element={<Dashboard />} /> */}
                {/*   <Route path="details" element={<EventDetailsCoordinator />} /> */}
                {/*   <Route */}
                {/*     path="new-request/details" */}
                {/*     element={<EventDetailsCoordinator isBeforeApprove />} */}
                {/*   /> */}
                {/*   <Route */}
                {/*     path="estimates/view" */}
                {/*     element={<EventDetailsCoordinator isBeforeApprove />} */}
                {/*   /> */}
                {/* </Route> */}
                {/* <Route */}
                {/*   path="positions" */}
                {/*   element={<Positions readOnly={!auth?.canPositionEdit} />} */}
                {/* /> */}
                {/* <Route */}
                {/*   path="contractors" */}
                {/*   element={ */}
                {/*     <Contractors */}
                {/*       readOnly={{ */}
                {/*         addContractor: false, */}
                {/*         editContractor: true, */}
                {/*         deleteContractor: true, */}
                {/*       }} */}
                {/*     /> */}
                {/*   } */}
                {/* /> */}
                {/* <Route path="contractors/add" element={<AddContractors />} /> */}
                {/* <Route path="find-sow" element={<FindSow />} /> */}
                <Route path="/notifications" element={<Notifications />} />
                {/* <Route path="configuration"> */}
                {/*   <Route path="text-templates" element={<Configuration />} /> */}
                {/* </Route> */}
                {/* <Route */}
                {/*   path="configuration/tutorial-video" */}
                {/*   element={<TutorialVideo />} */}
                {/* /> */}
                {/* <Route path="/contract"> */}
                {/*   <Route */}
                {/*     path="new" */}
                {/*     element={<AllContractsClient type={"new"} />} */}
                {/*   /> */}
                {/*   <Route */}
                {/*     path="under-review" */}
                {/*     element={<AllContractsClient type={"under-review"} />} */}
                {/*   /> */}
                {/*   <Route */}
                {/*     path="verified" */}
                {/*     element={<AllContractsClient type={"verified"} />} */}
                {/*   /> */}
                {/* </Route> */}

                {/* <Route */}
                {/*   path="/contracts" */}
                {/*   element={<AllContractsClient type="All" />} */}
                {/* /> */}
                {/* <Route */}
                {/*   path="/contract-details" */}
                {/*   element={<ContractDetails isClient={true} />} */}
                {/* /> */}
                <Route path="profile" element={<ProfileFreelanceEc />} />
                {/* <Route path="generate-qr" element={<QRGeneration />} /> */}
              </Route>
            ) : null}
            {/* Freelance ec Routes */}

            {/* Contractor Routes */}
            {isActiveRole(USER_ROLES.CONTRACTOR) ? (
              <>
                <Route element={<LayoutContractor />}>
                  <Route path="booking-assistance" element={<BookingAssistancePage />} />
                  <Route path="support" element={<SupportChat />} />
                  <Route path="survey/:accessId" element={<SurveyResponsePage />} />
                  {!auth?.disableDashboard ? (
                    <Route path="/event">
                      <Route path="chat" element={<EventChatClient />} />

                      <Route path="chat/admin" element={<AdminChatClient />} />
                    </Route>
                  ) : null}

                  {/* /opportunities always registered — an invited contractor
                      (disableDashboard=true) needs to access it to respond to
                      invites before the admin approves. Optional children
                      for /opportunities/not-responded, /opportunities/applied. */}
                  <Route
                    path="/opportunities"
                    element={<ContractorAllOpportunities />}
                  >
                    <Route
                      path=":tab"
                      element={<ContractorAllOpportunities />}
                    />
                  </Route>

                  {!auth?.disableDashboard ? (
                    <Route path="/schedule" element={<Schedule />} />
                  ) : null}
                  {!auth?.disableDashboard ? (
                    <Route path="/notifications" element={<Notifications />} />
                  ) : null}
                  {!auth?.disableDashboard ? (
                    <Route
                      path="/resources"
                      element={<ContractorResources />}
                    />
                  ) : null}

                  {/* <Route path="/contract"> */}
                  {/*   <Route */}
                  {/*     path="new" */}
                  {/*     element={<AllContractsClient type={"new"} />} */}
                  {/*   /> */}
                  {/*   <Route */}
                  {/*     path="under-review" */}
                  {/*     element={<AllContractsClient type={"under-review"} />} */}
                  {/*   /> */}
                  {/*   <Route */}
                  {/*     path="verified" */}
                  {/*     element={<AllContractsClient type={"verified"} />} */}
                  {/*   /> */}
                  {/* </Route> */}

                  <Route
                    path="/contracts"
                    element={<AllContractsClient type="All" />}
                  />
                  <Route
                    path="/contract-details"
                    element={<ContractDetails isClient={true} />}
                  />
                  <Route path="qr-data" element={<QrData />} />
                  <Route
                    path="/safety-and-non-harassment-videos"
                    element={<SafetyAndHarassmentVideos />}
                  />
                  <Route
                    path="configuration/tutorial-video"
                    element={<TutorialVideo />}
                  />
                  <Route path="profile" element={<ProfileContractor />} />
                  {/* Onboarding checklist (2026-10-08) — always reachable. */}
                  <Route path="onboarding" element={<ContractorOnboarding />} />
                </Route>
                {/* FIX #4: contractors have no dashboard. Landing on /dashboard
                    (e.g. an old bookmark, an email deep-link, or a stale redirect)
                    fell through to the catch-all → bare 404 with no sidebar.
                    Redirect it to the contractor's real landing page instead. */}
                <Route
                  path="/dashboard"
                  element={<Navigate to="/opportunities" replace />}
                />
              </>
            ) : null}
            {/* Contractor Routes */}

            {/* Shareable Route */}
            {/* Email and push report links require login before the protected API
                checks event ownership and visibility in the active role. */}
            <Route
              path="/event-report/:reportId"
              element={
                <RequireAuthForShareableLink>
                  <EventReportDetail />
                </RequireAuthForShareableLink>
              }
            />
            {/* These three are opened straight from an email link. The route is public
                but the API behind it is not — logged out, the page used to render
                "This link is no longer valid" instead of a login screen. The wrapper
                parks the full URL in `emailRedirection` and sends the user to /login,
                which brings them right back here afterwards. */}
            <Route path="/show-media" element={<ShowMediaRoute />} />
            <Route path="/show" element={<ShowLinkRoute />} />
            <Route
              path="shareable/sow"
              element={
                <RequireAuthForShareableLink>
                  <EventSOW />
                </RequireAuthForShareableLink>
              }
            />
            <Route
              path="shareable/po"
              element={
                <RequireAuthForShareableLink>
                  <CoordinatorPO />
                </RequireAuthForShareableLink>
              }
            />
            <Route
              path="shareable/freelancerPO"
              element={
                <RequireAuthForShareableLink>
                  <FreelancePO />
                </RequireAuthForShareableLink>
              }
            />
            {/* /qr-data fallback outside the role gates — for any logged-in
                user who opens the QR link (a curious admin, coordinator, etc).
                The QrData page detects activeRole !== 'contractor' and renders
                a clear message instead of a silent loading state. Contractor
                still matches first via the <LayoutContractor> gate above.
                Without a JWT, /qr-handler redirects to /login beforehand. */}
            {auth?.userId ? (
              <Route path="qr-data" element={<QrData />} />
            ) : null}
            <Route path="shareable/estimate" element={<ShareableEstimate />} />
            <Route
              path="shareable/depositInvoice"
              element={<ShareableDepositInvoice />}
            />
            <Route path="shareable/invoice" element={<ShareableInvoice />} />
            <Route path="shareable/event-rates" element={<EventRates />} />
            <Route
              path="shareable/integration-rates"
              element={<EventRates tabValue={1} />}
            />
            {!auth?.userId ? (
              <Route path="contractor-invite" element={<ContractorInvite />} />
            ) : null}

            {/*
              Claim link for a contractor invited from Send Opportunities. Public
              and NOT gated on `auth?.userId`: the one-time token in the URL is the
              only credential, and the page must still work if a browser happens to
              hold someone else's stale session. Distinct from `contractor-invite`
              above, which is the self-signup wizard and creates a new account.
            */}
            <Route path="accept-invite" element={<AcceptInvite />} />

            {/* rates shareable routes */}
            {FRONTEND_CONFIG.ratePages ? (
              <Route
                path="live-events-rates"
                element={
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      minWidth: "1300px",
                      overflowX: "auto",
                      lineHeight: 1.5,
                      fontWeight: 400,
                      color: "rgba(255, 255, 255, 0.87)",
                      fontFamily: "Roboto, sans-serif",
                    }}
                  >
                    <LiveEvent />
                  </Box>
                }
              />
            ) : null}
            {FRONTEND_CONFIG.ratePages ? (
              <Route
                path="integrations-rates"
                element={
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      minWidth: "1300px",
                      overflowX: "auto",
                      lineHeight: 1.5,
                      fontWeight: 400,
                      color: "rgba(255, 255, 255, 0.87)",
                      fontFamily: "Roboto, sans-serif",
                    }}
                  >
                    <Integration />
                  </Box>
                }
              />
            ) : null}
            {FRONTEND_CONFIG.contractorConsentPages && false ? (
              <Route path="/consent">
                <Route
                  path="media-release-1099"
                  element={<MediaReleaseConsent1099 />}
                />
                <Route
                  path="payment-terms-acknowledgement-1099"
                  element={<PaymentTermsAcknowledgementConsent1099 />}
                />
                <Route
                  path="text-communication-1099"
                  element={<TextCommunicationConsent1099 />}
                />
                <Route
                  path="independent-contractor-aggreement-1099"
                  element={<IndependentContractorAgreementConsent1099 />}
                />
                <Route
                  path="backstage-pass-1099"
                  element={<BackStagePassConsent1099 />}
                />
                <Route
                  path="electronic-execution-1099"
                  element={<ElectronicExecutionConsent1099 />}
                />
              </Route>
            ) : null}

            {/* Path alias: `/all-events` (raw, legacy/typed by hand) →
                `/event/all-events`. All 4 roles register the approved list
                at `/event/all-events`; without this alias the raw URL matched
                no <Route> and fell through to the catch-all, getting stuck at
                "Loading…" for an authenticated user. */}
            <Route
              path="/all-events"
              element={<Navigate to="/event/all-events" replace />}
            />

            {/* Catch-all: if the user has a token (authenticated but Routes
                haven't picked up the URL yet — e.g. during a role switch, before the
                new activeRole's Route block mounts), show a spinner instead
                of a flashing 404. Only show the real NotFound when logged out.
                AuthedRouteFallback falls through to NotFound after a short grace so it
                doesn't spin forever on a URL that never matches. */}
            <Route
              path="*"
              element={
                auth?.userToken ? <AuthedRouteFallback /> : <NotFound />
              }
            />
          </Routes>
          <AlertDialog />
          <ReportModal />
          {isUserRole(
            USER_ROLES.SUPERADMIN,
            USER_ROLES.ADMIN,
            USER_ROLES.COORDINATOR,
          ) && <SmsHistoryEventModal />}
          {isUserRole(
            USER_ROLES.SUPERADMIN,
            USER_ROLES.ADMIN,
            USER_ROLES.COORDINATOR,
          ) && (
            <>
              <DirectSMSModal />
              <DocAndContractsViewer />
              <ManageApprovals />
            </>
          )}
          <EventAdjustmentWrapper />
        </Suspense>
      </ThemeProvider>
      {/* Phones only — renders nothing on desktop. Outside <Routes> so it applies
          site-wide, and MUI-free like SandboxBanner. Last in the DOM on purpose:
          it is visually at the bottom, so screen readers and Tab should reach it
          after the page content, not before it. */}
      <OpenInAppBanner />
      {/* New page starts at the top (sidebar links, navigate()). */}
      <ScrollToTopOnNavigate />
    </div>
  );
};

export default App;
